/**
 * resolveSelectQueueContext — derives the `useCellQueueJoin` window context for
 * `UnifiedSelectPopover`'s own-held single-cell queue-join slot (CU-86cbejhae,
 * "Dodaj wpis dla innego klienta"): joining the waitlist for a different
 * client than the one holding the caller's own reserved-own/option-own cell.
 *
 * Mirrors `resolveCellQueueWindowContext`'s INSPECT-side resolution
 * (`resolve-cell-queue-window-context.ts` in `@ooh/screens`), but is
 * fed by the SELECT selection's own already-resolved
 * `UnifiedSelectCellResolution` — the exact same per-surface `resolveCell`
 * function every surface already builds for Rezerwacja/Opcja/Zwolnij/the
 * deadline field — instead of a raw `CellEntry[]` grid lookup. Only ever
 * active for exactly one selected cell that resolves an `ownReservation`;
 * a multi-cell selection or a free cell both fall back to the same inactive
 * shape `useCellQueueJoin` already treats as "no active hold".
 */
import type { MultiCellSelection, MultiSelectCellRef } from '../../availability';
import type { UnifiedSelectCellResolution } from '../../availability/lib/unified-select-types';
import { INACTIVE_CONTEXT } from './resolve-cell-queue-window-context';
import type { CellQueueWindowContext } from './use-cell-queue-join';

/** `Date` -> bare "YYYY-MM-DD" — matches the `dateFrom`/`dateTo` string format
 * every queue endpoint (`JoinQueueBodySchema`) and `useCellQueueJoin` context expect. */
function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function resolveSelectQueueContext(
  selection: MultiCellSelection | null,
  resolveCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null,
): CellQueueWindowContext {
  if (!selection || selection.cells.length !== 1) return INACTIVE_CONTEXT;
  const ref = selection.cells[0];
  if (!ref) return INACTIVE_CONTEXT;
  const resolved = resolveCell(ref);
  if (!resolved?.ownReservation) return INACTIVE_CONTEXT;
  return {
    mediaUnitId: resolved.mediaUnitId,
    dateFrom: toIsoDate(resolved.dateFrom),
    dateTo: toIsoDate(resolved.dateTo),
    enabled: true,
    ownership: 'mine',
  };
}
